import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  TextField,
  Divider,
  Alert,
  Button,
  Chip,
  Checkbox,
  FormControlLabel,
  Collapse
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export type ConflictResolutionStrategy = 'skip' | 'replace' | 'rename' | 'keep-both';

export interface Conflict {
  id: string;
  itemName: string;
  itemType: string;
  conflictType: 'name' | 'version' | 'exists';
  existingItem?: {
    name: string;
    version?: string;
    lastModified?: string;
    id?: string | number;
    shortname?: string;
    additionalMatches?: Array<{ name: string; id: string | number }>;
  };
  newItem: {
    name: string;
    version?: string;
  };
  resolution?: ConflictResolutionStrategy;
  newName?: string;
  selectedMatchesToDelete?: string[]; // IDs of specific courses to delete when replacing
}

export interface ConflictResolutionPanelProps {
  title?: string;
  conflicts: Conflict[];
  onResolve: (conflictId: string, resolution: ConflictResolutionStrategy, newName?: string, selectedMatchesToDelete?: string[]) => void;
  onResolveAll?: (resolution: ConflictResolutionStrategy) => void;
  allowBulkResolution?: boolean;
}

const getConflictDescription = (conflict: Conflict): string => {
  switch (conflict.conflictType) {
    case 'name':
      return `An item with the name "${conflict.itemName}" already exists`;
    case 'version':
      return `Version ${conflict.newItem.version} conflicts with existing version ${conflict.existingItem?.version}`;
    case 'exists':
      return `This item already exists at the destination`;
    default:
      return 'Conflict detected';
  }
};

export const ConflictResolutionPanel: React.FC<ConflictResolutionPanelProps> = ({
  title = 'Resolve Conflicts',
  conflicts,
  onResolve,
  onResolveAll,
  allowBulkResolution = true
}) => {
  const [bulkResolution, setBulkResolution] = React.useState<ConflictResolutionStrategy>('skip');
  const [expandedConflicts, setExpandedConflicts] = React.useState<Set<string>>(new Set());
  const [selectedMatches, setSelectedMatches] = React.useState<Record<string, Set<string>>>({});

  const unresolvedCount = conflicts.filter(c => !c.resolution).length;
  const hasUnresolved = unresolvedCount > 0;

  const toggleExpanded = (conflictId: string) => {
    setExpandedConflicts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conflictId)) {
        newSet.delete(conflictId);
      } else {
        newSet.add(conflictId);
      }
      return newSet;
    });
  };

  const handleMatchSelection = (conflictId: string, matchId: string, checked: boolean) => {
    setSelectedMatches(prev => {
      const newSelections = { ...prev };
      if (!newSelections[conflictId]) {
        newSelections[conflictId] = new Set();
      } else {
        newSelections[conflictId] = new Set(newSelections[conflictId]);
      }

      if (checked) {
        newSelections[conflictId].add(matchId);
      } else {
        newSelections[conflictId].delete(matchId);
      }

      return newSelections;
    });
  };

  const handleSelectAllMatches = (conflictId: string, conflict: Conflict, checked: boolean) => {
    setSelectedMatches(prev => {
      const newSelections = { ...prev };
      if (checked && conflict.existingItem) {
        const allIds = new Set<string>();
        // Add primary match
        if (conflict.existingItem.id) {
          allIds.add(String(conflict.existingItem.id));
        }
        // Add additional matches
        if (conflict.existingItem.additionalMatches) {
          conflict.existingItem.additionalMatches.forEach(match => {
            allIds.add(String(match.id));
          });
        }
        newSelections[conflictId] = allIds;
      } else {
        newSelections[conflictId] = new Set();
      }
      return newSelections;
    });
  };

  const handleResolve = (conflictId: string, resolution: ConflictResolutionStrategy, newName?: string) => {
    const selectedIds = selectedMatches[conflictId] ? Array.from(selectedMatches[conflictId]) : undefined;
    onResolve(conflictId, resolution, newName, selectedIds);
  };

  const handleApplyToAll = () => {
    if (onResolveAll) {
      onResolveAll(bulkResolution);
    } else {
      conflicts.forEach(conflict => {
        if (!conflict.resolution) {
          onResolve(conflict.id, bulkResolution);
        }
      });
    }
  };

  if (conflicts.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Alert severity="success">
          No conflicts detected. You can proceed safely.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {hasUnresolved ? (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
          Found {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} that need{conflicts.length === 1 ? 's' : ''} resolution.
          {` ${unresolvedCount} unresolved.`}
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 2 }}>
          All conflicts have been resolved. You can proceed to the next step.
        </Alert>
      )}

      {allowBulkResolution && hasUnresolved && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" gutterBottom fontWeight="bold">
            Bulk Resolution
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
            <Select
              value={bulkResolution}
              onChange={(e) => setBulkResolution(e.target.value as ConflictResolutionStrategy)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="skip">Skip All</MenuItem>
              <MenuItem value="replace">Replace All</MenuItem>
              <MenuItem value="rename">Rename All</MenuItem>
              <MenuItem value="keep-both">Keep Both</MenuItem>
            </Select>
            <Button variant="outlined" size="small" onClick={handleApplyToAll}>
              Apply to All Unresolved
            </Button>
          </Box>
        </Box>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Conflict</TableCell>
              <TableCell>Resolution</TableCell>
              <TableCell>New Name (if renaming)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conflicts.map((conflict) => (
              <TableRow key={conflict.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {conflict.itemName}
                  </Typography>
                  {conflict.newItem.version && (
                    <Chip label={`v${conflict.newItem.version}`} size="small" sx={{ mt: 0.5 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={conflict.itemType} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {getConflictDescription(conflict)}
                  </Typography>
                  {conflict.existingItem && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                      <Typography variant="caption" fontWeight="bold" color="text.primary" display="block">
                        Existing course in destination:
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Name: {conflict.existingItem.name}
                      </Typography>
                      {conflict.existingItem.shortname && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Short name: {conflict.existingItem.shortname}
                        </Typography>
                      )}
                      {conflict.existingItem.id && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          ID: {conflict.existingItem.id}
                        </Typography>
                      )}
                      {conflict.existingItem.version && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Version: {conflict.existingItem.version}
                        </Typography>
                      )}
                      {conflict.existingItem.additionalMatches && conflict.existingItem.additionalMatches.length > 0 && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'warning.main' }}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                            onClick={() => toggleExpanded(conflict.id)}
                          >
                            <Typography variant="caption" fontWeight="bold" color="error.main">
                              ⚠️ Also matches {conflict.existingItem.additionalMatches.length} more course(s)
                            </Typography>
                            {expandedConflicts.has(conflict.id) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </Box>

                          <Collapse in={expandedConflicts.has(conflict.id)}>
                            <Box sx={{ mt: 1 }}>
                              {conflict.resolution === 'replace' && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" fontWeight="bold" color="text.primary" display="block" sx={{ mb: 0.5 }}>
                                    Select which courses to replace:
                                  </Typography>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={
                                          selectedMatches[conflict.id]?.size ===
                                          (1 + (conflict.existingItem.additionalMatches?.length || 0))
                                        }
                                        onChange={(e) => handleSelectAllMatches(conflict.id, conflict, e.target.checked)}
                                      />
                                    }
                                    label={<Typography variant="caption">Select all</Typography>}
                                  />
                                </Box>
                              )}

                              {/* Primary match */}
                              {conflict.resolution === 'replace' ? (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selectedMatches[conflict.id]?.has(String(conflict.existingItem.id)) || false}
                                      onChange={(e) => handleMatchSelection(conflict.id, String(conflict.existingItem.id), e.target.checked)}
                                    />
                                  }
                                  label={
                                    <Typography variant="caption" color="text.secondary">
                                      {conflict.existingItem.name} (ID: {conflict.existingItem.id})
                                    </Typography>
                                  }
                                  sx={{ display: 'block', ml: 1 }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 1 }}>
                                  • {conflict.existingItem.name} (ID: {conflict.existingItem.id})
                                </Typography>
                              )}

                              {/* Additional matches */}
                              {conflict.existingItem.additionalMatches.map((match, idx) => (
                                conflict.resolution === 'replace' ? (
                                  <FormControlLabel
                                    key={idx}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={selectedMatches[conflict.id]?.has(String(match.id)) || false}
                                        onChange={(e) => handleMatchSelection(conflict.id, String(match.id), e.target.checked)}
                                      />
                                    }
                                    label={
                                      <Typography variant="caption" color="text.secondary">
                                        {match.name} (ID: {match.id})
                                      </Typography>
                                    }
                                    sx={{ display: 'block', ml: 1 }}
                                  />
                                ) : (
                                  <Typography key={idx} variant="caption" color="text.secondary" display="block" sx={{ pl: 1 }}>
                                    • {match.name} (ID: {match.id})
                                  </Typography>
                                )
                              ))}

                              {conflict.resolution === 'replace' && (
                                <Typography variant="caption" color="info.main" display="block" sx={{ mt: 1 }}>
                                  Selected: {selectedMatches[conflict.id]?.size || 0} course(s) will be deleted
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    value={conflict.resolution || ''}
                    onChange={(e) => handleResolve(conflict.id, e.target.value as ConflictResolutionStrategy)}
                    size="small"
                    displayEmpty
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="" disabled>
                      Choose...
                    </MenuItem>
                    <MenuItem value="skip">Skip</MenuItem>
                    <MenuItem value="replace">Replace</MenuItem>
                    <MenuItem value="rename">Rename</MenuItem>
                    <MenuItem value="keep-both">Keep Both</MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  {conflict.resolution === 'rename' ? (
                    <TextField
                      size="small"
                      placeholder="Enter new name"
                      value={conflict.newName || ''}
                      onChange={(e) => onResolve(conflict.id, 'rename', e.target.value)}
                      sx={{ minWidth: 200 }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">
          Resolved: {conflicts.length - unresolvedCount} / {conflicts.length}
        </Typography>
      </Box>
    </Paper>
  );
};
